import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { useAuth0 } from "@auth0/auth0-react";
import PageLayout from "../layout/PageLayout";
import { InvaderService, InvaderDetails } from "../../services/InvaderService";
import { InvaderStatusLabels, InvaderStatusColors, isValidStatus } from "../../constants/InvaderStatus";
import "leaflet/dist/leaflet.css";
import "./InvaderDetailPage.css";

const markerIcon = (captured: boolean) => {
    const color = captured ? '#008000' : '#3333ff';
    const markup = renderToStaticMarkup(
        <svg xmlns="http://www.w3.org/2000/svg" fill={color} viewBox="0 0 24 24" width="40px" height="40px">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 4.25 6 11 6 11s6-6.75 6-11c0-3.87-3.13-7-6-7z"/>
        </svg>
    );
    return L.divIcon({
        html: markup,
        className: 'custom-div-icon',
        iconSize: [40, 40],
        iconAnchor: [20, 40],
    });
};

const formatDate = (dateString: string | null): string => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

const InvaderDetailPage = () => {
    const { invaderId } = useParams<{ invaderId: string }>();
    const [invader, setInvader] = useState<InvaderDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { getAccessTokenSilently } = useAuth0();

    useEffect(() => {
        if (!invaderId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        InvaderService.fetchInvaderDetails(getAccessTokenSilently, invaderId)
            .then((data) => {
                if (!cancelled) setInvader(data);
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof Error ? err.message : "Erreur inconnue");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [invaderId, getAccessTokenSilently]);

    if (loading) {
        return (
            <PageLayout>
                <div className="invader-detail-container">
                    <div className="invader-detail-loading">
                        <div className="loader"></div>
                        <span>Chargement de {invaderId}...</span>
                    </div>
                </div>
            </PageLayout>
        );
    }

    if (error || !invader) {
        return (
            <PageLayout>
                <div className="invader-detail-container">
                    <div className="invader-detail-error">
                        <span className="invader-detail-error__icon">👾</span>
                        <p>{error || "Invader introuvable"}</p>
                        <Link to="/invaders" className="invader-detail-back">← Retour aux invaders</Link>
                    </div>
                </div>
            </PageLayout>
        );
    }

    const images = [
        { url: invader.imageUrl, credit: invader.imageUrlCredit, label: "Image officielle" },
        { url: invader.bgImageUrl, credit: invader.bgImageCredit, label: "Contexte" },
        { url: invader.additionalImageUrl, credit: invader.additionalImageCredit, label: "Complémentaire" },
    ].filter(img => img.url);

    const statusLabel = isValidStatus(invader.status) ? InvaderStatusLabels[invader.status] : invader.status;
    const statusColor = isValidStatus(invader.status) ? InvaderStatusColors[invader.status] : 'var(--text-muted)';

    return (
        <PageLayout>
            <div className="invader-detail-container">
                {/* Terminal Window Header */}
                <div className="invader-detail-terminal">
                    <div className="invader-detail-terminal__header">
                        <div className="invader-detail-terminal__dots">
                            <span className="invader-detail-terminal__dot invader-detail-terminal__dot--red"></span>
                            <span className="invader-detail-terminal__dot invader-detail-terminal__dot--yellow"></span>
                            <span className="invader-detail-terminal__dot invader-detail-terminal__dot--green"></span>
                        </div>
                        <span className="invader-detail-terminal__title">{invader.id.toLowerCase()}</span>
                    </div>
                    <div className="invader-detail-terminal__body">
                        <div className="invader-detail-titlebar">
                            <h1 className="invader-detail-title">{invader.id}<span className="cursor-blink"></span></h1>
                            <Link to="/invaders" className="invader-detail-back">← Invaders</Link>
                        </div>
                        <p className="invader-detail-subtitle">
                            {invader.cityName || "Ville inconnue"}
                            {invader.captured && invader.dateFlash && ` — flashé le ${formatDate(invader.dateFlash)}`}
                        </p>
                    </div>
                </div>

                {/* Stats */}
                <div className="invader-detail-stats">
                    <div className="invader-detail-stat">
                        <span className="invader-detail-stat__value invader-detail-stat__value--gold">
                            {invader.value != null && invader.value >= 0 ? invader.value : "?"}
                        </span>
                        <span className="invader-detail-stat__label">Points</span>
                    </div>
                    <div className="invader-detail-stat">
                        <span className="invader-detail-stat__value" style={{ color: statusColor }}>
                            {statusLabel}
                        </span>
                        <span className="invader-detail-stat__label">État</span>
                    </div>
                    <div className="invader-detail-stat">
                        <span className="invader-detail-stat__value invader-detail-stat__value--green">
                            {invader.captured ? "✓" : "✗"}
                        </span>
                        <span className="invader-detail-stat__label">{invader.captured ? "Capturé" : "Non capturé"}</span>
                    </div>
                </div>

                {/* Hint */}
                {invader.hint && (
                    <p className="invader-detail-hint">💡 {invader.hint}</p>
                )}

                {/* Images */}
                {images.length > 0 && (
                    <div className="invader-detail-gallery">
                        {images.map(img => (
                            <figure key={img.label} className="invader-detail-figure">
                                <img
                                    src={img.url!}
                                    alt={`${invader.id} — ${img.label}`}
                                    className="invader-detail-image"
                                    loading="lazy"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = '/invader.svg';
                                    }}
                                />
                                <figcaption className="invader-detail-caption">
                                    {img.label}
                                    {img.credit && <span className="invader-detail-credit"> — © {img.credit}</span>}
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                )}

                {/* Liens externes */}
                {(invader.videoLocationUrl || invader.instagramUrl) && (
                    <div className="invader-detail-links">
                        {invader.videoLocationUrl && (
                            <a
                                href={invader.videoLocationUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="invader-detail-link"
                            >
                                🎬 Vidéo du lieu
                                {invader.videoLocationCredit && ` (© ${invader.videoLocationCredit})`}
                            </a>
                        )}
                        {invader.instagramUrl && (
                            <a
                                href={invader.instagramUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="invader-detail-link"
                            >
                                📷 Instagram
                            </a>
                        )}
                    </div>
                )}

                {/* Carte */}
                <div className="invader-detail-map">
                    <MapContainer
                        center={[invader.lat, invader.lng]}
                        zoom={16}
                        scrollWheelZoom={false}
                        style={{ height: "100%", width: "100%" }}
                    >
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        <Marker position={[invader.lat, invader.lng]} icon={markerIcon(invader.captured)} />
                    </MapContainer>
                </div>
            </div>
        </PageLayout>
    );
};

export default InvaderDetailPage;
